import { MembershipPlanId } from '../pricing/membershipPlanId'
import { MembershipsV1BillingCycle, MembershipsV1Currency } from '@sdk/Api'
import { number } from 'svelte-i18n'
import { get } from 'svelte/store'

interface CurrencyPrices {
	[MembershipsV1Currency.CURRENCY_USD]: string
	[MembershipsV1Currency.CURRENCY_EUR]: string
}

interface BillingCycles {
	[MembershipsV1BillingCycle.BILLING_CYCLE_MONTHLY]?: CurrencyPrices
	[MembershipsV1BillingCycle.BILLING_CYCLE_ANNUAL]?: CurrencyPrices
}

interface RevenueIntervalData {
	revenueCapTopInterval: number
	[MembershipPlanId.STARTER]?: BillingCycles
	[MembershipPlanId.BUSINESS]?: BillingCycles
	[MembershipPlanId.ENTERPRISE]?: BillingCycles
	[MembershipPlanId.NETWORK]?: BillingCycles
}

export interface PricingTableRow {
	lowerBound: number
	upperBound: number
	revenueRange: string
	monthlyPayment: string
	overcharges: string
	link?: string
}

export function generateTable(
	data: RevenueIntervalData[],
	planId: MembershipPlanId,
	billingCycle: MembershipsV1BillingCycle,
	isUsd: boolean,
): PricingTableRow[] {
	const tableRows: PricingTableRow[] = []
	let lowerBound = 0
	let overcharge = 9.0
	let lastPrice: string | null = null
	const currencyKey = isUsd
		? MembershipsV1Currency.CURRENCY_USD
		: MembershipsV1Currency.CURRENCY_EUR

	const createRow = (upperBound: number, price: string): PricingTableRow => ({
		lowerBound,
		upperBound,
		revenueRange: `Up to $${get(number)(upperBound)}`,
		monthlyPayment: price,
		overcharges: `$${overcharge.toFixed(1)}`,
	})

	// Find data points that actually contain the requested planId and billingCycle
	const relevantData = data.filter((item) => item[planId]?.[billingCycle])

	for (const item of relevantData) {
		const upperBound = item.revenueCapTopInterval
		lastPrice = item[planId][billingCycle][currencyKey]

		tableRows.push(createRow(upperBound, lastPrice))

		lowerBound = upperBound + 1
		overcharge = Math.max(0, overcharge - 1.0)
	}

	// Add the "Request a Quote" row for revenue above the highest tier
	if (relevantData.length > 0) {
		const lastRevenueInterval = relevantData[relevantData.length - 1].revenueCapTopInterval
		tableRows.push({
			lowerBound,
			upperBound: Infinity,
			revenueRange: `$${get(number)(lastRevenueInterval)}+`,
			monthlyPayment: 'Request a Quote',
			overcharges: 'Request a Quote',
			link: 'https://trackdesk.com/request-demo', // TODO: use environment variable
		})
	}

	return tableRows
}
